import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Loader2, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [step, setStep] = useState(1); // 1: Email input, 2: Confirmation
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (!email) {
      setEmailError("Please enter your email");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }

    setEmailError("");
    setAuthError("");
    setIsLoading(true);

    const { error } = await resetPassword(email);

    if (error) {
      setAuthError(error.message);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setStep(2);
    }
  };

  const handleConfirm = () => {
    // Navigate to login page after confirmation
    navigate("/login");
  };

  return (
    <div>
      {/* Header */}
      <Header showBack={true} onBackClick={() => navigate(-1)} />

      <div className="p-4">
        <div className="w-full max-w-md mx-auto flex flex-col gap-6 md:gap-10 mt-6 md:mt-12">
          {step === 1 ? (
            <>
              {/* Step 1: Email Input */}
              <h2 className="text-2xl font-bold text-foreground">
                Forgot your password?
              </h2>

              <p className="text-xs text-foreground/80 leading-relaxed">
                Enter your email to receive a password reset link.
              </p>

              <form onSubmit={handleSendEmail} className="space-y-10">
                <div className="space-y-1">
                  <Label
                    htmlFor="email"
                    className="text-xs text-muted-foreground font-normal"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder=""
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    className="h-12 px-4 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
                    required
                  />
                  {emailError && (
                    <p className="text-[10px] text-red-500">{emailError}</p>
                  )}
                </div>

                {authError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {authError}
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              {/* Step 2: Confirmation */}
              <div className="flex flex-col items-center gap-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>

                <h2 className="text-2xl font-bold text-foreground">
                  Email Sent!
                </h2>

                <p className="text-sm text-foreground/80 text-center leading-relaxed">
                  We've sent a password reset link to:
                  <br />
                  <span className="font-medium text-foreground">{email}</span>
                </p>

                <p className="text-xs text-muted-foreground text-center">
                  Please check your email and follow the instructions to reset
                  your password.
                </p>

                <Button onClick={handleConfirm} className="w-full h-12 mt-4">
                  Back to Login
                </Button>
              </div>
            </>
          )}

          {/* Footer */}
          <Footer />
        </div>
      </div>
    </div>
  );
}
